<template>
  <div class="operate-config-page">
    <a-card :bordered="false" title="巨量回传">
      <div class="toolbar">
        <a-input
          v-model:value="query.keyword"
          placeholder="名称/事件/资产/备注"
          allow-clear
          style="width: 220px"
          @pressEnter="fetchList"
        />
        <a-input v-model:value="query.asset" placeholder="资产ID" allow-clear style="width: 160px" />
        <a-input-number
          v-model:value="query.appId"
          placeholder="应用ID"
          :min="1"
          style="width: 120px"
        />
        <a-button type="primary" @click="fetchList">查询</a-button>
        <a-button @click="resetQuery">重置</a-button>
      </div>

      <a-table
        :columns="columns"
        :data-source="list"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        size="small"
        bordered
        :scroll="{ x: 'max-content' }"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'defaultCallback'">
            <a-tag :color="record.defaultCallback ? 'blue' : 'orange'">
              {{ record.defaultCallback ? '默认比例' : '分时比例' }}
            </a-tag>
          </template>
          <template v-if="column.dataIndex === 'action'">
            <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="modalVisible"
      title="编辑巨量回传"
      width="980px"
      :confirm-loading="saving"
      @ok="handleSave"
      @cancel="modalVisible = false"
    >
      <a-form :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-divider orientation="left">基础信息</a-divider>
        <a-row :gutter="12">
          <a-col :span="12">
            <a-form-item label="ID">
              <a-input-number v-model:value="form.id" disabled style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="应用ID">
              <a-input-number v-model:value="form.appId" :min="1" style="width: 100%" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item label="名称">
          <a-input v-model:value="form.name" allow-clear />
        </a-form-item>
        <a-row :gutter="12">
          <a-col :span="12">
            <a-form-item label="支付事件">
              <a-input v-model:value="form.event" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="签约事件">
              <a-input v-model:value="form.agreementEvent" allow-clear />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="12">
          <a-col :span="12">
            <a-form-item label="退订事件">
              <a-input v-model:value="form.cancelEvent" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="资产ID">
              <a-input v-model:value="form.asset" allow-clear />
            </a-form-item>
          </a-col>
        </a-row>

        <a-divider orientation="left">回传比例</a-divider>
        <a-row :gutter="12">
          <a-col :span="8">
            <a-form-item label="计算方式">
              <a-select v-model:value="form.callbackRateCalType">
                <a-select-option :value="0">纯随机比例</a-select-option>
                <a-select-option :value="1">已上报次数</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="默认%">
              <a-input-number v-model:value="form.callbackRate" :min="0" :max="100" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="模式">
              <a-switch
                v-model:checked="form.defaultCallback"
                checked-children="默认"
                un-checked-children="分时"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <div class="hour-rate-grid">
          <a-form-item
            v-for="hour in 24"
            :key="hour - 1"
            :label="`${hour - 1}时`"
          >
            <a-input-number
              v-model:value="form[`callbackRate${hour - 1}`]"
              :min="0"
              :max="100"
              style="width: 100%"
            />
          </a-form-item>
        </div>

        <a-divider orientation="left">计数和备注</a-divider>
        <a-row :gutter="12">
          <a-col :span="12">
            <a-form-item label="已上报支付">
              <a-input-number v-model:value="form.paidReported" :min="0" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="支付订单数">
              <a-input-number v-model:value="form.paidOrders" :min="0" style="width: 100%" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item label="备注">
          <a-textarea v-model:value="form.remark" :rows="3" allow-clear />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from "vue";
import { message } from "ant-design-vue";
import { get, postJson } from "@/utils/request";
import { analyticsUrl } from "@/utils/api";

const query = reactive({
  keyword: "",
  asset: "",
  appId: undefined,
});

const loading = ref(false);
const saving = ref(false);
const modalVisible = ref(false);
const list = ref([]);
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`,
});

const columns = [
  { title: "ID", dataIndex: "id", width: 80, fixed: "left" },
  { title: "名称", dataIndex: "name", width: 180 },
  { title: "应用", dataIndex: "appName", width: 180 },
  { title: "应用ID", dataIndex: "appId", width: 90 },
  { title: "资产ID", dataIndex: "asset", width: 150 },
  { title: "支付事件", dataIndex: "event", width: 140 },
  { title: "签约事件", dataIndex: "agreementEvent", width: 140 },
  { title: "退订事件", dataIndex: "cancelEvent", width: 140 },
  { title: "默认%", dataIndex: "callbackRate", width: 90 },
  { title: "比例模式", dataIndex: "defaultCallback", width: 100 },
  { title: "已上报支付", dataIndex: "paidReported", width: 110 },
  { title: "支付订单数", dataIndex: "paidOrders", width: 110 },
  { title: "备注", dataIndex: "remark", width: 160 },
  { title: "更新时间", dataIndex: "updatedAt", width: 170 },
  { title: "操作", dataIndex: "action", width: 90, fixed: "right" },
];

const form = reactive({
  id: undefined,
  name: "",
  appId: undefined,
  event: "",
  agreementEvent: "",
  cancelEvent: "",
  asset: "",
  paidReported: 0,
  paidOrders: 0,
  callbackRateCalType: 0,
  callbackRate: 100,
  defaultCallback: true,
  remark: "",
});

for (let hour = 0; hour < 24; hour += 1) {
  form[`callbackRate${hour}`] = 100;
}

const resetQuery = () => {
  query.keyword = "";
  query.asset = "";
  query.appId = undefined;
  pagination.current = 1;
  fetchList();
};

const fetchList = async () => {
  loading.value = true;
  try {
    const json = await get(analyticsUrl("/operate/ocean-engine-conversion/list"), {
      pageIdx: pagination.current,
      pageSize: pagination.pageSize,
      keyword: query.keyword,
      asset: query.asset,
      appId: query.appId,
    });
    if (json.code === 200) {
      list.value = json.data?.data || [];
      pagination.total = json.data?.dataSum || 0;
      pagination.current = json.data?.pageIdx || pagination.current;
      pagination.pageSize = json.data?.pageSize || pagination.pageSize;
    } else {
      message.error(json.content || "查询失败");
    }
  } finally {
    loading.value = false;
  }
};

const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchList();
};

const openEdit = (record) => {
  Object.assign(form, {
    id: record.id,
    name: record.name,
    appId: record.appId,
    event: record.event,
    agreementEvent: record.agreementEvent,
    cancelEvent: record.cancelEvent,
    asset: record.asset,
    paidReported: record.paidReported,
    paidOrders: record.paidOrders,
    callbackRateCalType: record.callbackRateCalType,
    callbackRate: record.callbackRate,
    defaultCallback: record.defaultCallback,
    remark: record.remark,
  });
  for (let hour = 0; hour < 24; hour += 1) {
    form[`callbackRate${hour}`] = record[`callbackRate${hour}`] ?? 100;
  }
  modalVisible.value = true;
};

const handleSave = async () => {
  if (!form.id) {
    message.warning("记录ID不能为空");
    return;
  }
  saving.value = true;
  try {
    const json = await postJson(analyticsUrl("/operate/ocean-engine-conversion/update"), { ...form });
    if (json.code === 200) {
      message.success("保存成功");
      modalVisible.value = false;
      fetchList();
    } else {
      message.error(json.content || "保存失败");
    }
  } finally {
    saving.value = false;
  }
};

onMounted(fetchList);
</script>

<style scoped>
.operate-config-page {
  padding: 24px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.hour-rate-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0 12px;
}

@media (max-width: 900px) {
  .hour-rate-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
